import React from "react";
//#region redux
import { AppState } from "store";
import { useDispatch, useSelector } from "react-redux";
import { incomingInvoiceAccepted, incomingInvoiceUpdated } from "store/actions/incomingInvoiceActions";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { IncomingInvoiceFooterProps } from "models/propTypes/incomingInvoicePropTypes";
import { IncomingInvoiceFormTypes } from "models/formTypes";
import { ActionButton } from "models/propTypes/commonPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { Icon, PageFooter } from "components/common";
import IncomingInvoiceMoreActions from "./moreActions";
import useEditableState from "./useReadOnlyState";
import useSendSupplierEmailDialog from "./forms/useSendEmailDialog";
import { useIncomingInvoiceService } from "serviceHooks";
import useIncomingInvoicePermissions from "./usePermissions";
import { useSaveCostForwardingMarking } from "components/costForwardingMarking";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import { IconDefinitionConstants } from "utils/constants";
import cssExports from "theme/styles/main.module.scss";

const IncomingInvoiceFooter: React.FC<IncomingInvoiceFooterProps> = (props) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const { reloadHandler, disableSuccessMessages } = props;
  const dispatch = useDispatch();
  const isReadOnly = useEditableState(props.invoice.state);
  const currentState = props.invoice.state;

  const loggedInUserId = useSelector((store: AppState) => store.auth.loggedInUser.personId);
  const { getIncomingInvoiceUserPermissions, getHasIncomingInvoiceSigneePermission } = useIncomingInvoicePermissions();
  const hasSigneePermission = getHasIncomingInvoiceSigneePermission();
  const saveWithCostForwardingMarking = useSaveCostForwardingMarking("incomingInvoice");

  const acceptInvoice = () => {
    return props.invoiceFormSubmitHandler((values) => {
      return saveWithCostForwardingMarking(props.invoice.id, values, () =>
        incomingInvoiceService.onAccept(incomingInvoiceService.getUpdatedInvoice(props.invoice, values))
      );
    });
  };

  // Skips the call when the user left every address field blank, so an existing
  // supplier address is never overwritten with empty values.
  const updateSupplierPostalAddress = async (
    values: IncomingInvoiceFormTypes.Details | IncomingInvoiceFormTypes.Todo
  ) => {
    const supplierId = props.invoice.supplier?.id;
    if (!supplierId || !props.isInternationalPayment) {
      return;
    }

    const postalAddress = incomingInvoiceService.getUpdatedSupplierPostalAddress(values);
    const hasAddressData = postalAddress.street || postalAddress.city || postalAddress.zipCode || postalAddress.country;

    if (hasAddressData) {
      await incomingInvoiceService.onUpdateSupplierPostalAddress(supplierId, postalAddress);
    }
  };

  const acceptInvoiceAndPaymentDetails = () => {
    return props.invoiceFormSubmitHandler(async (values) => {
      const updatedInvoice = incomingInvoiceService.getUpdatedInvoice(props.invoice, values);
      const updatePaymentDetails = incomingInvoiceService.getUpdatedPaymentDetails(values);

      // First update payment details to validate BIC before approving the invoice
      // This ensures that if the payment details update (including BIC validation) fails, the invoice won't be approved
      await incomingInvoiceService.onUpdatePaymentDetails(props.invoice.id, updatePaymentDetails);

      // The address update must succeed before approval can proceed
      await updateSupplierPostalAddress(values);

      // Only approve the invoice after both payment details and supplier address (if needed) are successfully updated
      return saveWithCostForwardingMarking(props.invoice.id, values, () =>
        incomingInvoiceService.onAccept(updatedInvoice)
      );
    });
  };

  const getSupplierEmailInfo = () => {
    return incomingInvoiceService.getSendSupplierEmailInfo(props.invoice.id);
  };

  const updateInvoice = () => {
    return props.invoiceFormSubmitHandler(async (values) => {
      if (props.isInternationalPayment && hasSigneePermission) {
        const updatePaymentDetails = incomingInvoiceService.getUpdatedPaymentDetails(values);
        await incomingInvoiceService.onUpdatePaymentDetails(props.invoice.id, updatePaymentDetails);

        await updateSupplierPostalAddress(values);
      }

      return saveWithCostForwardingMarking(props.invoice.id, values, () =>
        incomingInvoiceService.save(props.invoice.id, incomingInvoiceService.getUpdatedInvoice(props.invoice, values))
      );
    });
  };

  const onInvoiceAccepted = () => {
    reloadHandler(
      (incomingInvoice) => {
        if (
          incomingInvoice.state === WintSuperkollAPIModel.IncomingInvoiceState.Received ||
          (incomingInvoice.state === WintSuperkollAPIModel.IncomingInvoiceState.Signee &&
            getHasIncomingInvoiceSigneePermission(true))
        ) {
          dispatch(incomingInvoiceUpdated(incomingInvoice, loggedInUserId, getIncomingInvoiceUserPermissions()));
        } else {
          dispatch(incomingInvoiceAccepted(props.invoice.id));
        }
      },
      () => dispatch(incomingInvoiceAccepted(props.invoice.id))
    );
  };

  const onSendEmailCanceled = async () => {
    try {
      await incomingInvoiceService.sendEmailToSupplier({
        incomingInvoiceId: props.invoice.id,
        confirmEmailSending: false,
      });
    } finally {
      onInvoiceAccepted();
    }
  };

  const onInvoiceUpdated = () => {
    reloadHandler((r) => dispatch(incomingInvoiceUpdated(r, loggedInUserId, getIncomingInvoiceUserPermissions())));
  };

  const { showDialog } = useSendSupplierEmailDialog(props.invoice.id, onInvoiceAccepted, onSendEmailCanceled);

  const onShowInternationalPaymentView = async () => {
    props.onShowInternationalPaymentView && props.onShowInternationalPaymentView();
  };

  const onClickAccept = async () => {
    const accept = props.isInternationalPayment && hasSigneePermission ? acceptInvoiceAndPaymentDetails : acceptInvoice;

    const [acceptInvoiceResponse, emailInfo] = await Promise.all([accept(), getSupplierEmailInfo()]);
    const supplierEmailInfo = {
      to: emailInfo.emailSuggestions?.length ? emailInfo.emailSuggestions[0] : "",
      subject: emailInfo.subject ?? "",
      message: emailInfo.body ?? "",
    };
    emailInfo.shouldSendEmailToSupplier
      ? showDialog(supplierEmailInfo, emailInfo.emailSuggestions ?? [])
      : onInvoiceAccepted();
    return acceptInvoiceResponse;
  };

  const getActionButtons = (): ActionButton[] => {
    const buttons: ActionButton[] = [];
    if (!isReadOnly) {
      !props.hideSave &&
        buttons.push({
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
          onClick: updateInvoice,
          type: StyleEnums.DisplayType.Info,
          successMessage: TranslationKeys.IncomingInvoice.UpdateSuccessMessage,
          onActionSuccess: onInvoiceUpdated,
        });

      const approvementTextChangeByInvoiceState =
        currentState === WintSuperkollAPIModel.IncomingInvoiceState.Received
          ? TranslationKeys.Common.Approve
          : currentState === WintSuperkollAPIModel.IncomingInvoiceState.Signee
            ? TranslationKeys.Common.FinalApproval
            : TranslationKeys.Common.Accept;

      if (
        hasSigneePermission &&
        props.isTodoCardStep &&
        props.isInternationalPayment &&
        !props.isInternationalPaymentView
      ) {
        buttons.push({
          text: (
            <>
              {LocalizationHelperInstance.getFormattedMessage(approvementTextChangeByInvoiceState)}
              {<Icon className={cssExports.footerButtonMargingLeft} icon={IconDefinitionConstants.WChevronRight} />}
            </>
          ),
          onClick: onShowInternationalPaymentView,
          type: StyleEnums.DisplayType.Success,
          successMessage: TranslationKeys.IncomingInvoice.ApproveSuccessMessage,
        });
      } else {
        buttons.push({
          text: LocalizationHelperInstance.getFormattedMessage(approvementTextChangeByInvoiceState),
          onClick: onClickAccept,
          type: StyleEnums.DisplayType.Success,
          successMessage: TranslationKeys.IncomingInvoice.ApproveSuccessMessage,
          isDisabled: props.disableAccept,
          showTooltip: props.showAcceptButtonTooltip,
          tooltipText: props.acceptButtonTooltipText,
        });
      }
    }
    return buttons;
  };

  return (
    <PageFooter
      actions={getActionButtons()}
      moreActions={
        <IncomingInvoiceMoreActions
          invoice={props.invoice}
          reloadHandler={props.reloadHandler}
          markAsDoneHandler={props.markAsDoneHandler}
          onUpdatePaymentDialogSuccessHandler={props.onUpdatePaymentDialogSuccessHandler}
          costForwardingAction={props.costForwardingAction}
        />
      }
      showNavigateBack={props.showNavigateBack}
      disableSuccessMessages={disableSuccessMessages}
    />
  );
};

export default IncomingInvoiceFooter;
